import {Component, EventEmitter, Input, OnChanges, OnInit, Output} from '@angular/core';
import {FormControl, FormGroup} from "@angular/forms";
import {
  AllergenGroup,
  AllergenGroupOverride,
  AllergenGroupRequestDto,
  emptyOverride,
  MenuSection,
  NameTranslationType,
  OverrideInstruction,
  OverridePosition,
  OverridePositionRequest,
  OverridePositionsMap,
  PositionTypeEnum,
} from "app/models/menu.model";
import {Organization} from "app/models/organization.model";
import {Store} from "@ngrx/store";
import {AllergenGroupApiService} from "app/services/menu-edit/allergen-group-api.service";
import * as MenuSelectors from "app/reducers/menu.selectors";
import {MenuOverrideService} from "app/services/menu-override.service";
import {MenuService} from "app/services/menu.service";
import {TranslateService} from "@ngx-translate/core";
import {UntilDestroy, untilDestroyed} from "@ngneat/until-destroy";

@UntilDestroy()
@Component({
  selector: 'app-allergen-group-form',
  templateUrl: './allergen-group-form.component.html',
  styleUrls: ['./allergen-group-form.component.scss']
})
export class AllergenGroupFormComponent implements OnInit, OnChanges {
  @Input() allergenGroupId: string | null = null;
  @Input() allergenGroup: AllergenGroup;
  @Input() originalAllergenGroup: AllergenGroup;
  @Input() organization: Organization;
  @Input() menuSectionId: string;
  @Input() menuId: string;
  @Input() allowOverrides: boolean;
  @Input() allergenGroups: AllergenGroup[] = [];
  @Input() menuSections: MenuSection[] = [];
  @Input() isVisible = false;

  @Output() formResult = new EventEmitter<any>();
  @Output() closePanel = new EventEmitter<void>();

  initValues: Record<string, string> = {}
  form: FormGroup;
  overridePositionsMap: OverridePositionsMap;
  selectedAllergenGroupImgSrc: string;
  allergenGroupNames: Set<string> = new Set();
  selectedSectionIds: Set<string> = new Set();
  selectAllChecked = false;

  constructor(
      private store: Store,
      private translate: TranslateService,
      private menuService: MenuService,
      private allergenGroupApiService: AllergenGroupApiService,
  ) {
  }

  ngOnInit(): void {
    this.initForm();
    this.subscribeToOverrides();
  }

  ngOnChanges(changes: any): void {
    if (changes['allergenGroupId'] && !changes['allergenGroupId'].firstChange) {
      this.initForm();
    }
  }

  private subscribeToOverrides(): void {
    if (!this.isManuallyCreated()) {
      this.store.select(MenuSelectors.selectOverridePositionsMap)
          .pipe(untilDestroyed(this))
          .subscribe(overridePositionsMap => {
            this.overridePositionsMap = overridePositionsMap;
          });
    }
  }

  private initForm(): void {
    this.allergenGroupNames.clear();
    for (const ag of this.allergenGroups) {
      if (ag.name !== this.allergenGroup.name) {
        this.allergenGroupNames.add(ag.name);
      }
    }
    this.setInitValue('allergenGroupName', this.allergenGroup.name);
    this.setInitValue('picture', this.allergenGroup.externalPictureUrl ?? '');
    this.setInitValue('textColor', this.allergenGroup.textColor ?? '');
    this.setInitValue('backgroundColor', this.allergenGroup.backgroundColor ?? '');

    this.form = new FormGroup({});

    this.form.addControl('textColor', new FormControl(this.allergenGroup.textColor ?? ''));
    this.form.addControl('backgroundColor', new FormControl(this.allergenGroup.backgroundColor ?? ''));
    this.form.addControl('picture', new FormControl(this.allergenGroup.externalPictureUrl ?? ''));
    this.form.addControl('allergenGroupName', new FormControl(this.allergenGroup.name ?? ''));
    this.form.addControl('isManuallyCreated', new FormControl({value: this.isManuallyCreated(), disabled: true}));

    if(!this.allergenGroup.nameTranslations){
      this.allergenGroup.nameTranslations = {} as NameTranslationType;
    }

    for(const lang of this.organization.additionalLanguages){
      this.setInitValue(lang, this.allergenGroup.nameTranslations[lang] ?? '');
      this.form.addControl(lang, new FormControl(this.allergenGroup.nameTranslations[lang] ?? ''))
    }
    this.selectedAllergenGroupImgSrc = this.allergenGroup.externalPictureUrl ?? '';
    this.selectedSectionIds = new Set([this.menuSectionId]);
    this.selectAllChecked = false;
  }

  private setInitValue(key: string, value: string): void {
    this.initValues[key] = value;
  }

  toggleSection(sectionId: string): void {
    if (this.selectedSectionIds.has(sectionId)) {
      this.selectedSectionIds.delete(sectionId);
    } else {
      this.selectedSectionIds.add(sectionId);
    }
    this.selectAllChecked = this.selectedSectionIds.size === this.menuSections.length;
  }

  toggleSelectAll(): void {
    this.selectAllChecked = !this.selectAllChecked;
    if (this.selectAllChecked) {
      this.selectedSectionIds = new Set(this.menuSections.map(s => s.id));
    } else {
      this.selectedSectionIds = new Set([this.menuSectionId]);
    }
  }

  async submit(): Promise<void> {
    if(!this.validate()){
      return;
    }
    const requestDto = this.createRequestDto();

    if (this.allergenGroupId == null) {
      const response = await this.allergenGroupApiService.createAllergenGroupAsync(this.menuId, requestDto);
      this.formResult.emit({created: {...response, externalPictureUrl: this.getPictureValue()}});
      return;
    }

    if (this.isManuallyCreated()) {
      const response = await this.allergenGroupApiService.updateAllergenGroupAsync(this.allergenGroupId, requestDto);
      await this.saveAllergenGroupOverrides();
      this.formResult.emit({updated: {...response, externalPictureUrl: this.getPictureValue()}});
    } else {
      this.formResult.emit({updated: {...this.allergenGroup, ...requestDto, externalPictureUrl: this.getPictureValue()}});
      await this.saveAllergenGroupOverrides();
    }
  }

  private createRequestDto(): AllergenGroupRequestDto {
    const translations: NameTranslationType = {};
    for (const lang of this.organization.additionalLanguages) {
      translations[lang] = this.getFormValue(lang);
    }
    return {
      name: this.getFormValue('allergenGroupName'),
      nameTranslations: translations,
      textColor: this.getFormValue('textColor'),
      backgroundColor: this.getFormValue('backgroundColor'),
    };
  }

  private getFormValue(controlName: string): string {
    return this.form.get(controlName)?.value ?? '';
  }

  private getPictureValue(): string {
    return this.form.get('picture')?.value ?? '';
  }

  async saveAllergenGroupOverrides(): Promise<void> {
    if (this.selectedSectionIds.size === 0) {
      return;
    }

    const positionId = this.allergenGroupId;
    const existingPosition = this.overridePositionsMap?.getOverride(PositionTypeEnum.AllergenGroups, positionId);
    const hasExistingOverride = !!existingPosition && existingPosition.override !== emptyOverride;

    const existingOverrideObj = hasExistingOverride ? JSON.parse(existingPosition.override) : {};

    const overrideInstructions: OverrideInstruction[] = [];

    if (this.originalAllergenGroup.externalId) {
      this.collectChangedFields(overrideInstructions, hasExistingOverride, existingOverrideObj);
    }

    if (overrideInstructions.length === 0) {
      return;
    }

    const targetSectionIds = [...this.selectedSectionIds];
    const overridePosition: OverridePosition = existingPosition
        ? { ...existingPosition, menuSectionId: this.menuSectionId }
        : { positionType: PositionTypeEnum.AllergenGroups, positionId, override: emptyOverride, menuSectionId: this.menuSectionId };

    const request: OverridePositionRequest = {
      ...overridePosition,
      instructions: overrideInstructions,
    };

    await this.menuService.saveOverridePositions(this.menuSectionId, [request], targetSectionIds)
        .then(_ => _)
        .catch(error => alert(error.error));
  }

  private collectChangedFields(
      instructions: OverrideInstruction[],
      isOverrideExists: boolean,
      existingOverrideObj: AllergenGroupOverride,
  ): void {
    if (this.initValues['allergenGroupName']?.trim() !== this.getFormValue('allergenGroupName')?.trim()) {
      MenuOverrideService.overrideTextFiled(
          this.allergenGroup, this.originalAllergenGroup, 'name',
          this.getFormValue('allergenGroupName'), isOverrideExists, instructions);
    }

    if (this.initValues['textColor']?.trim() !== this.getFormValue('textColor')?.trim()) {
      MenuOverrideService.overrideTextFiled(
          this.allergenGroup, this.originalAllergenGroup, 'textColor',
          this.getFormValue('textColor'), isOverrideExists, instructions);
    }

    if (this.initValues['backgroundColor']?.trim() !== this.getFormValue('backgroundColor')?.trim()) {
      MenuOverrideService.overrideTextFiled(
          this.allergenGroup, this.originalAllergenGroup, 'backgroundColor',
          this.getFormValue('backgroundColor'), isOverrideExists, instructions);
    }

    if (this.organization.additionalLanguages) {
      for (const lang of this.organization.additionalLanguages) {
        if (this.initValues[lang] !== this.getFormValue(lang)) {
          MenuOverrideService.overrideTranslationFiled(
              this.allergenGroup, this.originalAllergenGroup, 'nameTranslations', lang,
              this.getFormValue(lang), isOverrideExists, existingOverrideObj, instructions);
        }
      }
    }

    if (this.initValues['picture'] !== this.getPictureValue()) {
      MenuOverrideService.overrideTextFiled(
          this.allergenGroup, this.originalAllergenGroup, 'pictureUrl',
          this.selectedAllergenGroupImgSrc, isOverrideExists, instructions, true);
    }
  }

  async deleteItem(): Promise<void> {
    if(confirm(this.translate.instant('MENU.ALLERGEN_GROUP.CONFIRM_DELETE'))){
      await this.allergenGroupApiService.delete(this.allergenGroupId);
      this.formResult.emit({deleted: this.allergenGroupId})
    }
  }

  isManuallyCreated(): boolean {
    return !this.allergenGroup.externalId;
  }

  canHaveOverrides(): boolean {
    return !this.isManuallyCreated();
  }

  isFieldDifferent(controlName: string, originalValue: string): boolean {
    return this.getFormValue(controlName) !== originalValue;
  }

  restoreField(event: Event, controlName: string, originalValue: string): void {
    event.stopPropagation();
    this.form.get(controlName)?.setValue(originalValue);
  }

  isTranslationDifferent(lang: string): boolean {
    return this.getFormValue(lang) !== (this.originalAllergenGroup.nameTranslations?.[lang] ?? '');
  }

  restoreTranslation(event: Event, lang: string): void {
    event.stopPropagation();
    const originalValue = this.originalAllergenGroup.nameTranslations?.[lang] ?? '';
    this.form.get(lang)?.setValue(originalValue);
  }

  isPictureDifferent(): boolean {
    return this.selectedAllergenGroupImgSrc !== (this.originalAllergenGroup.externalPictureUrl ?? '');
  }

  restorePicture(event: Event): void {
    event.stopPropagation();
    const originalUrl = this.originalAllergenGroup.externalPictureUrl ?? '';
    this.selectedAllergenGroupImgSrc = originalUrl;
    this.form.get('picture')?.setValue(originalUrl);
  }

  onImageChanged(newImage: string): void {
    this.form.get('picture').setValue(newImage);
    this.selectedAllergenGroupImgSrc = newImage;
  }

  validate(): boolean {
    const tagName = this.getFormValue('allergenGroupName');
    if(!tagName){
      this.form.setErrors({'allergenGroupName': "Name is required"});
      return false;
    }
    if(this.allergenGroupNames.has(tagName)){
      this.form.setErrors({'allergenGroupName': "Name should be unique"});
      return false;
    }
    return true;
  }

  onClose(): void {
    this.closePanel.emit();
  }
}

export interface AllergenGroupFormDataType {
  allergenGroupId: string | null,
  allergenGroup: AllergenGroup,
  originalAllergenGroup: AllergenGroup,
  organization: Organization,
  menuSectionId: string,
  menuId: string,
  allowOverrides: boolean,
  allergenGroups: AllergenGroup[],
  menuSections: MenuSection[],
}
